import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  getAdminUserMessages,
  getChat,
  getGroupChatMessages,
} from "../../api/message.api";
import { MessageRow, MessagesWithPagination } from "../../api/message.type";
import { ChatType } from "../../api/messageViews.type";
import { StreamCallbacks } from "../event-streaming/useEventStreamLifecycle";
import { useEventStreamingControls } from "../EventStreamingWrapper";
import { useUserContext } from "../UserContextWrapper";
import { useMessageChannel } from "./MessageChannelContext";
import { MessageContextType } from "./types";
import { useAssistantDrafts } from "./useAssistantDrafts";
import { useMessageMutations } from "./useMessageMutations";
import { useMessagePagination } from "./useMessagePagination";
import { useMessageSync } from "./useMessageSync";

export function useMessageContextValue(): MessageContextType {
  const { user } = useUserContext();
  const { startStreaming, registerStreamCallbacks } =
    useEventStreamingControls();

  const [isTyping, setIsTyping] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<MessageRow | null>(null);
  const [text, setText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [onMessageFound, setOnMessageFound] = useState<
    ((messageId: string, attempts: number) => void) | undefined
  >(undefined);

  const { chatType, groupChatId, adminUserId, setChannel } = useMessageChannel();

  const isGroupChat = chatType === "group_chat";
  const isAdminChat = chatType === "all";

  const getMessagesFn = useCallback((): Promise<MessagesWithPagination> => {
    if (isGroupChat) {
      if (!groupChatId) {
        return Promise.resolve({
          messages: [],
          totalCount: 0,
          nextPage: null,
        });
      }
      return getGroupChatMessages(groupChatId, 1, 50);
    }
    if (isAdminChat) {
      if (!adminUserId) {
        return Promise.resolve({
          messages: [],
          totalCount: 0,
          nextPage: null,
        });
      }
      return getAdminUserMessages(adminUserId, 1, 50);
    }
    return getChat(1, 50);
  }, [adminUserId, groupChatId, isAdminChat, isGroupChat]);

  const queryKey = useMemo(
    () =>
      isGroupChat
        ? ["group-chat-messages", groupChatId]
        : isAdminChat
        ? ["admin-chat-messages", adminUserId]
        : ["chat-messages", user?.id, chatType],
    [adminUserId, chatType, groupChatId, isAdminChat, isGroupChat, user?.id]
  );

  const queryEnabled = isGroupChat
    ? !!groupChatId
    : isAdminChat
    ? !!adminUserId
    : !!user?.id && chatType === "ai_chat";

  const {
    messages,
    setMessages,
    totalCount,
    setTotalCount,
    syncMessages,
    resetMessages,
  } = useMessageSync({
    queryKey,
    queryEnabled,
  });

  const clearPendingImagesRef = useRef<() => void>(() => {});

  useEffect(() => {
    resetMessages();
  }, [adminUserId, chatType, groupChatId, resetMessages]);

  const streamingChannelKey = useMemo(
    () =>
      isGroupChat
        ? `group:${groupChatId ?? "unknown"}`
        : isAdminChat
        ? `admin:${adminUserId ?? "unknown"}`
        : `ai:${user?.id ?? "anonymous"}`,
    [adminUserId, groupChatId, isAdminChat, isGroupChat, user?.id]
  );

  const registerStreamCallbacksWithChannel = useCallback(
    (channelKey: string, callbacks: StreamCallbacks | null) => {
      // The underlying registerStreamCallbacks doesn't support channel keys yet,
      // so we register the callbacks directly. In the future, this could be enhanced
      // to support multiple channels.
      registerStreamCallbacks(callbacks);
    },
    [registerStreamCallbacks]
  );

  useAssistantDrafts({
    isGroupChat,
    groupChatId,
    userId: user?.id,
    channelKey: streamingChannelKey,
    registerStreamCallbacks: registerStreamCallbacksWithChannel,
    syncMessages,
    setIsTyping,
  });

  const { sendMessage, clearPendingImages } = useMessageMutations({
    isGroupChat,
    groupChatId,
    userId: user?.id,
    syncMessages,
    setIsTyping,
    setPendingMessage,
    startStreaming,
    text,
    setText,
  });

  useEffect(() => {
    clearPendingImagesRef.current = clearPendingImages;
  }, [clearPendingImages]);

  const { isLoading, isFetching, refetch, data } =
    useQuery<MessagesWithPagination>({
      queryFn: getMessagesFn,
      queryKey,
      enabled: queryEnabled,
      refetchOnWindowFocus: true,
    });

  const paginationScope = useMemo(
    () =>
      isGroupChat
        ? `group:${groupChatId ?? ""}`
        : isAdminChat
        ? `admin:${adminUserId ?? ""}`
        : `chat:${chatType}`,
    [adminUserId, chatType, groupChatId, isAdminChat, isGroupChat]
  );

  const safeMessages = Array.isArray(messages) ? messages : [];

  const { loadMoreMessages, loadMessagesUntilId } = useMessagePagination({
    isGroupChat,
    groupChatId,
    isAdminChat,
    adminUserId,
    hasMoreMessages,
    setHasMoreMessages,
    currentPage,
    setCurrentPage,
    setIsLoadingMore,
    isLoading,
    isLoadingMore,
    setMessages,
    onMessageFound,
    messages: safeMessages,
    resetScopeKey: paginationScope,
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    if (
      (isGroupChat && chatType === "group_chat") ||
      (isAdminChat && chatType === "all") ||
      (!isGroupChat && !isAdminChat && chatType === "ai_chat")
    ) {
      const nextMessages = Array.isArray(data.messages) ? data.messages : [];
      setMessages(nextMessages);
      setHasMoreMessages((data?.nextPage ?? null) !== null);
      setCurrentPage(1);
      const nextTotalCount =
        typeof data.totalCount === "number"
          ? data.totalCount
          : nextMessages.length;
      setTotalCount(nextTotalCount);
      setPendingMessage(null);
      clearPendingImagesRef.current();
    } else {
      console.log("🔍 Ignoring query result - chat type changed during query");
    }
  }, [
    data,
    isGroupChat,
    isAdminChat,
    chatType,
    setMessages,
    setHasMoreMessages,
    setCurrentPage,
    setTotalCount,
    setPendingMessage,
  ]);

  const setChatTypeHandler = useCallback(
    (type: ChatType, id?: string) => {
      setChannel(type, id);
      setCurrentPage(1);
      setIsLoadingMore(false);
    },
    [setChannel]
  );

  const setOnMessageFoundHandler = useCallback(
    (callback: ((messageId: string, attempts: number) => void) | undefined) => {
      setOnMessageFound(callback);
    },
    []
  );

  const refreshMessages = useCallback(async () => {
    try {
      await refetch();
    } catch (error) {
      console.warn("refreshMessages error", error);
    }
  }, [refetch]);

  return useMemo(
    () => ({
      messages: safeMessages,
      setMessages,
      isTyping,
      setIsTyping,
      isLoading: isLoading || isFetching,
      sendMessage,
      pendingMessage,
      setPendingMessage,
      text,
      setText,
      setChatType: setChatTypeHandler,
      loadMoreMessages,
      loadMessagesUntilId,
      hasMoreMessages,
      currentPage,
      isLoadingMore,
      chatType,
      groupChatId,
      adminUserId,
      totalCount,
      onMessageFound,
      setOnMessageFound: setOnMessageFoundHandler,
      refreshMessages,
    }),
    [
      safeMessages,
      setMessages,
      isTyping,
      setIsTyping,
      isLoading,
      isFetching,
      sendMessage,
      pendingMessage,
      setPendingMessage,
      text,
      setText,
      setChatTypeHandler,
      loadMoreMessages,
      loadMessagesUntilId,
      hasMoreMessages,
      currentPage,
      isLoadingMore,
      chatType,
      groupChatId,
      adminUserId,
      totalCount,
      onMessageFound,
      setOnMessageFoundHandler,
      refreshMessages,
    ]
  ) satisfies MessageContextType;
}
